import { useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "@/components/Icon";
import { Screen } from "@/components/Layout";
import { Toggle, Spinner } from "@/components/Controls";
import { colors, spacing, radius, fonts, type } from "@/theme";
import { createPersona } from "@/api";
import { useWebRecorder } from "@/hooks/useWebRecorder";

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function PersonaSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const st = location.state || {};
  const userId = st.userId || "";
  const firstName = st.firstName || "your loved one";
  const familyToken = st.token || undefined;

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [status, setStatus] = useState(null);
  const [attestedBy, setAttestedBy] = useState("");
  const [consentLiving, setConsentLiving] = useState(false);
  const [consentAuthorized, setConsentAuthorized] = useState(false);
  const [consentSignature, setConsentSignature] = useState("");
  const [speakingStyle, setSpeakingStyle] = useState("");
  const [comfortWords, setComfortWords] = useState("");
  const [topicsLeanIn, setTopicsLeanIn] = useState("");
  const [topicsAvoid, setTopicsAvoid] = useState("");
  const [modeLocked, setModeLocked] = useState(false);

  const recorder = useWebRecorder();
  const [audio, setAudio] = useState(null); // {blob, name, duration}
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const toggleRecord = async () => {
    setError(null);
    if (recorder.recording) {
      const blob = await recorder.stop();
      if (blob) setAudio({ blob, name: "voice-sample.webm", duration: recorder.elapsed });
    } else {
      setAudio(null);
      await recorder.start();
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setAudio({ blob: f, name: f.name || "sample.webm", duration: 0 });
  };

  const submit = async () => {
    if (!audio || !status) return;
    setSubmitting(true); setError(null);
    const res = await createPersona(userId, {
      name: name.trim(), relationship: relationship.trim(), status,
      attested_by: attestedBy.trim(), has_consent: consentLiving, is_authorized: consentAuthorized,
      consent_signature: consentSignature.trim(), speaking_style: speakingStyle.trim(),
      comfort_words: comfortWords.trim(), topics_lean_in: topicsLeanIn.trim(),
      topics_avoid: topicsAvoid.trim(), mode_locked: modeLocked, audio,
    }, familyToken);
    setSubmitting(false);
    if (res.ok) navigate("/family-portal", { replace: true, state: { userId, firstName, token: familyToken } });
    else setError(res.error || "Something went wrong.");
  };

  const canStep1 = name.trim().length > 0 && !!status;
  const canStep2 = status === "living" ? consentLiving && consentSignature.trim().length > 0 : consentAuthorized;
  const canSubmit = !!audio && !submitting;
  const nextDisabled = step === 1 ? !canStep1 : step === 2 ? !canStep2 : false;

  return (
    <Screen testID="persona-setup-screen" maxWidth={640} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.sm}px ${spacing.md}px` }}>
        <button data-testid="persona-back" onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))} style={{ width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="chevron-left" size={30} color={colors.onSurface} />
        </button>
        <span style={{ ...fonts.displaySemiBold, fontSize: type.lg, color: colors.onSurface }}>Add a familiar voice</span>
        <div style={{ width: 48 }} />
      </div>
      <div style={{ display: "flex", gap: spacing.sm, justifyContent: "center", paddingBottom: spacing.md }}>
        {[1, 2, 3, 4].map((n) => <div key={n} style={{ width: 40, height: 6, borderRadius: 3, background: n <= step ? colors.brandPrimary : colors.surfaceTertiary }} />)}
      </div>

      <div style={{ flex: 1, padding: `${spacing.sm}px ${spacing.lg}px 160px` }}>
        {step === 1 && (
          <>
            <StepTitle>Who is this voice?</StepTitle>
            <StepSub>This lets {firstName} hear a familiar, comforting voice during their calls.</StepSub>
            <Label>Their name or what {firstName} calls them</Label>
            <input data-testid="persona-name-input" style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Mom, Grandpa Joe" />
            <Label top>Relationship (optional)</Label>
            <input data-testid="persona-relationship-input" style={input} value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. daughter, late husband" />
            <Label top>Is this person living or has passed?</Label>
            <div style={{ display: "flex", gap: spacing.md, marginTop: spacing.sm }}>
              {[["living", "user", "Living"], ["deceased", "heart", "Has passed"]].map(([key, ic, lbl]) => (
                <button key={key} data-testid={`status-${key}`} onClick={() => setStatus(key)} className="ember-press"
                  style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, minHeight: 72, borderRadius: radius.lg, border: `2px solid ${status === key ? colors.brandPrimary : colors.border}`, background: status === key ? colors.brandPrimary : colors.surfaceSecondary }}>
                  <Icon name={ic} size={22} color={status === key ? colors.onBrandPrimary : colors.brand} />
                  <span style={{ ...fonts.textBold, fontSize: type.lg, color: status === key ? colors.onBrandPrimary : colors.brand }}>{lbl}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <StepTitle>A moment of care</StepTitle>
            <StepSub>Recreating a voice is deeply personal. We only continue with proper consent.</StepSub>
            <div style={{ display: "flex", gap: spacing.md, background: colors.brandTertiary, borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
              <Icon name="shield" size={26} color={colors.brand} />
              <span style={{ flex: 1, ...fonts.textSemiBold, fontSize: type.base, color: colors.onBrandTertiary, lineHeight: 1.5 }}>
                {status === "living" ? "Because this person is living, they must personally agree to have their voice recreated." : "Because this person has passed, only someone authorized by the family should recreate their voice, and with love and respect."}
              </span>
            </div>
            {status === "deceased" && (
              <div data-testid="grief-advisory" style={{ display: "flex", gap: spacing.md, background: colors.surfaceSecondary, borderRadius: radius.lg, border: `2px solid ${colors.brandTertiary}`, padding: spacing.lg, marginBottom: spacing.lg }}>
                <Icon name="heart" size={22} color={colors.brand} />
                <span style={{ flex: 1, ...fonts.text, fontSize: type.base, color: colors.onSurface, lineHeight: 1.5 }}>Hearing a loved one&rsquo;s voice again can bring real comfort — and sometimes unexpected waves of grief. Please go gently. {firstName} can always switch back to the everyday companion voice.</span>
              </div>
            )}
            {status === "living" ? (
              <div data-testid="living-consent-block">
                <div style={{ display: "flex", gap: spacing.md, background: colors.brandTertiary, borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
                  <Icon name="smartphone" size={22} color={colors.brand} />
                  <span style={{ flex: 1, ...fonts.textSemiBold, fontSize: type.base, color: colors.onBrandTertiary, lineHeight: 1.5 }}>Please hand the device to {name.trim() || "this person"} — this consent must come from them directly.</span>
                </div>
                <Label>Your full name (to confirm it&rsquo;s you)</Label>
                <input data-testid="consent-signature-input" style={input} value={consentSignature} onChange={(e) => setConsentSignature(e.target.value)} placeholder="Type your full name" />
                <button data-testid="consent-living" onClick={() => setConsentLiving((v) => !v)} style={{ display: "flex", gap: spacing.md, alignItems: "flex-start", padding: `${spacing.md}px 0`, marginTop: spacing.lg, textAlign: "left", width: "100%" }}>
                  <Checkbox on={consentLiving} />
                  <span style={checkText}>I{consentSignature.trim() ? `, ${consentSignature.trim()},` : ""} agree to have my own voice recreated for this companion.</span>
                </button>
              </div>
            ) : (
              <button data-testid="consent-authorized" onClick={() => setConsentAuthorized((v) => !v)} style={{ display: "flex", gap: spacing.md, alignItems: "flex-start", padding: `${spacing.md}px 0`, textAlign: "left", width: "100%" }}>
                <Checkbox on={consentAuthorized} />
                <span style={checkText}>I am authorized by the family and act with respect for their memory.</span>
              </button>
            )}
            <Label top>Your name (for our records)</Label>
            <input data-testid="attested-by-input" style={input} value={attestedBy} onChange={(e) => setAttestedBy(e.target.value)} placeholder="e.g. Maria (daughter)" />
            <p style={{ ...fonts.text, fontSize: type.base, color: colors.muted, lineHeight: 1.4, marginTop: spacing.lg }}>The companion always remains an AI. It will never claim to actually be this person.</p>
          </>
        )}

        {step === 3 && (
          <>
            <StepTitle>What are they like?</StepTitle>
            <StepSub>A few gentle details help the companion feel like them — not just sound like them. All optional.</StepSub>
            <Label>How they speak / phrases they use</Label>
            <textarea data-testid="speaking-style-input" style={{ ...input, minHeight: 88, paddingTop: spacing.md }} value={speakingStyle} onChange={(e) => setSpeakingStyle(e.target.value)} placeholder="e.g. Warm and gentle. Often says 'sweetheart'." />
            <Label top>What they&rsquo;d say to comfort</Label>
            <textarea data-testid="comfort-words-input" style={{ ...input, minHeight: 88, paddingTop: spacing.md }} value={comfortWords} onChange={(e) => setComfortWords(e.target.value)} placeholder="e.g. 'Everything's going to be alright. I'm so proud of you.'" />
            <Label top>Topics to lean into</Label>
            <input data-testid="topics-lean-input" style={input} value={topicsLeanIn} onChange={(e) => setTopicsLeanIn(e.target.value)} placeholder="e.g. gardening, old family stories, faith" />
            <Label top>Topics to gently avoid</Label>
            <input data-testid="topics-avoid-input" style={input} value={topicsAvoid} onChange={(e) => setTopicsAvoid(e.target.value)} placeholder="e.g. the estate, a painful falling-out" />
            <div style={{ display: "flex", alignItems: "center", gap: spacing.md, background: colors.surfaceSecondary, borderRadius: radius.lg, padding: spacing.lg, marginTop: spacing.xl }}>
              <div style={{ flex: 1 }}>
                <div style={{ ...fonts.textBold, fontSize: type.lg, color: colors.onSurface }}>Lock to this voice</div>
                <div style={{ ...fonts.text, fontSize: type.base, color: colors.muted, lineHeight: 1.4, marginTop: 2 }}>{modeLocked ? `${firstName} will always hear this voice.` : `${firstName} can switch back to the everyday companion whenever they like.`}</div>
              </div>
              <Toggle testID="mode-lock-switch" value={modeLocked} onChange={setModeLocked} />
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <StepTitle>Add a voice sample</StepTitle>
            <StepSub>Record or upload 3–5 minutes of clear speech in a quiet room, one person only.</StepSub>
            <div style={{ background: colors.surfaceSecondary, borderRadius: radius.lg, padding: spacing.lg, marginBottom: spacing.xl }}>
              <div style={{ ...fonts.textBold, fontSize: type.lg, color: colors.onSurface, marginBottom: spacing.xs }}>For the best result</div>
              {["Find a quiet room with no background noise", "Speak naturally, as in a normal conversation", "Aim for at least 3 minutes (5 is even better)", "Only one person should be speaking"].map((t) => (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: spacing.sm, marginTop: spacing.sm }}>
                  <Icon name="check-circle" size={18} color={colors.success} /><span style={{ flex: 1, ...fonts.textSemiBold, fontSize: type.base, color: colors.muted }}>{t}</span>
                </div>
              ))}
            </div>
            {recorder.recording ? (
              <button data-testid="stop-recording" onClick={toggleRecord} className="ember-press" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, minHeight: 72, width: "100%", borderRadius: radius.lg, background: colors.error, marginBottom: spacing.md }}>
                <Icon name="square" size={28} color={colors.onError} /><span style={{ ...fonts.textExtraBold, fontSize: type.lg, color: colors.onError }}>Stop · {fmt(recorder.elapsed)}</span>
              </button>
            ) : (
              <button data-testid="start-recording" onClick={toggleRecord} className="ember-press" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, minHeight: 72, width: "100%", borderRadius: radius.lg, background: colors.brandPrimary, marginBottom: spacing.md }}>
                <Icon name="mic" size={28} color={colors.onBrandPrimary} /><span style={{ ...fonts.textExtraBold, fontSize: type.lg, color: colors.onBrandPrimary }}>Record now</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="audio/*" onChange={onFile} style={{ display: "none" }} />
            <button data-testid="pick-file" onClick={() => fileRef.current?.click()} className="ember-press" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, minHeight: 60, width: "100%", borderRadius: radius.lg, border: `2px solid ${colors.border}`, background: colors.surfaceSecondary }}>
              <Icon name="upload" size={22} color={colors.brand} /><span style={{ ...fonts.textBold, fontSize: type.base, color: colors.brand }}>Choose an audio file</span>
            </button>
            {recorder.permissionBlocked && <p style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.warning, marginTop: spacing.md }}>Microphone is off. Allow it in your browser, or upload a file instead.</p>}
            {audio && (
              <div data-testid="selected-audio" style={{ display: "flex", alignItems: "center", gap: spacing.md, background: colors.surfaceSecondary, borderRadius: radius.lg, padding: spacing.lg, marginTop: spacing.lg }}>
                <Icon name="check-circle" size={24} color={colors.success} />
                <span style={{ flex: 1, ...fonts.textBold, fontSize: type.base, color: colors.onSurface }}>{audio.duration ? `Recorded ${fmt(audio.duration)}` : audio.name}{audio.duration && audio.duration < 180 ? " · a bit short, 3 min is best" : ""}</span>
              </div>
            )}
            {error && (
              <div data-testid="persona-error" style={{ display: "flex", gap: spacing.sm, background: "#F7E4E4", borderRadius: radius.md, padding: spacing.md, marginTop: spacing.lg }}>
                <Icon name="alert-circle" size={20} color={colors.error} /><span style={{ flex: 1, ...fonts.textSemiBold, fontSize: type.base, color: colors.error, lineHeight: 1.4 }}>{error}</span>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ position: "sticky", bottom: 0, padding: `${spacing.md}px ${spacing.lg}px ${spacing.xl}px`, borderTop: `1px solid ${colors.divider}`, background: colors.surface }}>
        {step < 4 ? (
          <button data-testid="persona-next" disabled={nextDisabled} onClick={() => setStep(step + 1)} className="ember-press" style={{ ...cta, background: nextDisabled ? colors.border : colors.brandPrimary }}>
            <span style={ctaText}>Continue</span><Icon name="arrow-right" size={24} color={colors.onBrandPrimary} />
          </button>
        ) : (
          <button data-testid="persona-submit" disabled={!canSubmit} onClick={submit} className="ember-press" style={{ ...cta, background: !canSubmit ? colors.border : colors.brandPrimary }}>
            {submitting ? <Spinner color={colors.onBrandPrimary} /> : <><Icon name="heart" size={22} color={colors.onBrandPrimary} /><span style={ctaText}>Create voice</span></>}
          </button>
        )}
      </div>
    </Screen>
  );
}

const StepTitle = ({ children }) => <div style={{ ...fonts.displayBold, fontSize: type["2xl"], color: colors.onSurface, marginBottom: spacing.sm }}>{children}</div>;
const StepSub = ({ children }) => <div style={{ ...fonts.text, fontSize: type.lg, color: colors.muted, lineHeight: 1.5, marginBottom: spacing.xl }}>{children}</div>;
const Label = ({ children, top }) => <div style={{ ...fonts.textBold, fontSize: type.base, color: colors.onSurface, marginBottom: spacing.sm, marginTop: top ? spacing.lg : 0 }}>{children}</div>;
const Checkbox = ({ on }) => (
  <div style={{ width: 32, height: 32, borderRadius: radius.sm, border: `2px solid ${on ? colors.brandPrimary : colors.borderStrong}`, background: on ? colors.brandPrimary : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    {on && <Icon name="check" size={20} color={colors.onBrandPrimary} />}
  </div>
);

const input = { width: "100%", ...fonts.textSemiBold, fontSize: type.lg, color: colors.onSurface, background: colors.surfaceSecondary, borderRadius: radius.md, border: `2px solid ${colors.border}`, padding: `${spacing.md}px ${spacing.lg}px`, minHeight: 56 };
const checkText = { flex: 1, ...fonts.textSemiBold, fontSize: type.base, color: colors.onSurface, lineHeight: 1.5 };
const cta = { display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, minHeight: 64, width: "100%", borderRadius: radius.lg };
const ctaText = { ...fonts.textExtraBold, fontSize: type.xl, color: colors.onBrandPrimary };
